import { useParams, Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useProductBySlug } from '@/hooks/useProducts'
import { CountdownTimer } from '@/components/countdown-timer'
import { AddToCartButton } from '@/components/cart/add-to-cart-button'
import { VariantSelector } from '@/components/product/variant-selector'
import { CustomInputFields } from '@/components/product/custom-input-fields'
import { QuantitySelector } from '@/components/product/quantity-selector'
import { ProductMediaGallery } from '@/components/product/product-media-gallery'
import { ArrowLeft, Loader2, Shield, Truck, Lock, Clock } from 'lucide-react'
import { getPrimaryProductImage } from '@/lib/product-media'
import { ShareButton } from '@/components/share-button'
import { useState } from 'react'
import type { KeyFeatures, ProductSpecs, ProductOption, ProductVariant, CustomInputDefinition } from '@/types/database'
import { BRAND_NAME } from '@/lib/branding'

export function ProductPage() {
  const { slug } = useParams<{ slug: string }>()
  const { product, isLoading, error } = useProductBySlug(slug)
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null)
  const [customInputValues, setCustomInputValues] = useState<Record<string, string>>({})
  const [quantity, setQuantity] = useState(1)

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-zinc-500" />
      </div>
    )
  }

  if (error || !product) {
    return <Navigate to="/past-reviews" replace />
  }

  const isFeatured = product.status === 'featured'
  const discountPercent = isFeatured
    ? product.featured_discount_percent
    : product.archive_discount_percent

  // Parse JSONB fields
  const keyFeatures = (product.key_features as KeyFeatures) || []
  const specs = (product.specs as ProductSpecs) || {}
  const options = (product.options as unknown as ProductOption[]) || []
  const variants = (product.variants as unknown as ProductVariant[]) || []
  const customInputDefs = (product.custom_inputs as unknown as CustomInputDefinition[]) || []

  const hasOptions = options.length > 0 && options.some(o => o.values.length > 0)
  const hasCustomInputs = customInputDefs.length > 0

  // Calculate effective price (base + variant adjustment)
  const effectivePrice = product.original_price + (selectedVariant?.price_adjustment || 0)
  const discountedPrice = effectivePrice * (1 - discountPercent / 100)

  // Determine if add to cart should be disabled
  const needsVariant = hasOptions && !selectedVariant
  const addToCartDisabled = needsVariant || (selectedVariant ? !selectedVariant.available : false)

  // OG meta image
  const metaImage = getPrimaryProductImage(product)

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <>
      <Helmet>
        <title>{`${product.title} | ${BRAND_NAME}`}</title>
        <meta
          name="description"
          content={product.short_description || product.description || ''}
        />
        {metaImage && (
          <meta property="og:image" content={metaImage} />
        )}
      </Helmet>

      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-8">
          {/* Back link */}
          <Link
            to={isFeatured ? '/' : '/past-reviews'}
            className="mb-6 inline-flex items-center gap-1 text-sm text-zinc-500 transition-colors hover:text-zinc-900"
          >
            <ArrowLeft className="size-4" />
            Back to {isFeatured ? 'This Week' : 'Past Reviews'}
          </Link>

          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12">
            {/* Left Column - Media */}
            <ProductMediaGallery
              product={product}
              selectedVariant={selectedVariant}
              variant="detail"
            />

            {/* Right Column - Product Info */}
            <div className="flex flex-col">
              {/* Status Badge */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {isFeatured ? (
                  <span className="rounded-full bg-red-600 px-3 py-1 text-sm font-bold text-white">
                    This Week's Deal
                  </span>
                ) : (
                  <span className="rounded-full border border-zinc-300 bg-zinc-100 px-3 py-1 text-sm text-zinc-600">
                    Past Review
                  </span>
                )}
                {product.featured_end_date && !isFeatured && (
                  <span className="flex items-center gap-1 text-sm text-zinc-500">
                    <Clock className="size-3" />
                    Originally {formatDate(product.featured_end_date)}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="mb-3 text-3xl font-bold text-zinc-900 md:text-4xl">
                {product.title}
              </h1>

              {/* Short Description */}
              {product.short_description && (
                <p className="mb-6 text-lg text-zinc-600">
                  {product.short_description}
                </p>
              )}

              {/* Pricing */}
              <div className="mb-6 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-xl text-zinc-400 line-through">
                    ${effectivePrice.toFixed(2)}
                  </span>
                  <span className="text-4xl font-bold text-zinc-900">
                    ${discountedPrice.toFixed(2)}
                  </span>
                  <span className="rounded bg-red-600 px-3 py-1 text-sm font-bold text-white">
                    {discountPercent}% OFF
                  </span>
                </div>
                <p className="text-sm text-green-600 font-medium">
                  You save ${(effectivePrice - discountedPrice).toFixed(2)}
                </p>
              </div>

              {/* Countdown for featured */}
              {isFeatured && product.featured_end_date && (
                <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                  <p className="mb-2 text-center text-sm font-medium uppercase tracking-wider text-zinc-500">
                    Deal ends in
                  </p>
                  <CountdownTimer
                    targetDate={product.featured_end_date}
                    size="md"
                    variant="light"
                  />
                </div>
              )}

              {/* Variant Selector */}
              {hasOptions && (
                <div className="mb-6">
                  <VariantSelector
                    options={options}
                    variants={variants}
                    selectedVariantId={selectedVariant?.id || null}
                    onVariantChange={setSelectedVariant}
                  />
                </div>
              )}

              {/* Custom Input Fields */}
              {hasCustomInputs && (
                <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                  <h3 className="mb-3 text-sm font-semibold text-zinc-700">Personalization</h3>
                  <CustomInputFields
                    definitions={customInputDefs}
                    values={customInputValues}
                    onChange={setCustomInputValues}
                  />
                </div>
              )}

              {/* Quantity + CTA */}
              <div className="mb-6 space-y-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-zinc-700">Quantity</label>
                  <QuantitySelector value={quantity} onChange={setQuantity} />
                </div>
                <AddToCartButton
                  productId={product.id}
                  variantId={selectedVariant?.id ?? null}
                  quantity={quantity}
                  customInputs={hasCustomInputs ? customInputValues : undefined}
                  disabled={addToCartDisabled}
                  size="lg"
                  className="w-full text-lg font-bold sm:w-auto sm:px-12"
                >
                  {needsVariant
                    ? 'Select Options'
                    : `Add to Cart - $${discountedPrice.toFixed(2)}`
                  }
                </AddToCartButton>
              </div>

              {/* Trust Signals */}
              <div className="mb-4 flex flex-wrap gap-4 text-sm text-zinc-500">
                <div className="flex items-center gap-1.5">
                  <Lock className="size-4" />
                  <span>Secure checkout</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Truck className="size-4" />
                  <span>Free shipping</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Shield className="size-4" />
                  <span>Buyer protection</span>
                </div>
              </div>

              {/* Share */}
              <div className="mb-8">
                <ShareButton
                  url={`${window.location.origin}/review/${product.slug}`}
                  title={`${product.title} - ${discountPercent}% Off | ${BRAND_NAME}`}
                  description={product.short_description || `Check out ${product.title}`}
                  variant="button"
                />
              </div>

              {/* Key Features */}
              {keyFeatures.length > 0 && (
                <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                  <h3 className="mb-3 font-semibold text-zinc-900">Key Features</h3>
                  <ul className="space-y-2">
                    {keyFeatures.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-600">
                        <span className="mt-1.5 size-1.5 flex-shrink-0 rounded-full bg-red-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Specs */}
              {Object.keys(specs).length > 0 && (
                <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                  <h3 className="mb-3 font-semibold text-zinc-900">Specifications</h3>
                  <dl className="space-y-2">
                    {Object.entries(specs).map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <dt className="text-zinc-500">{key}</dt>
                        <dd className="text-zinc-700">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </div>

          {/* Full Description */}
          {product.description && (
            <div className="mx-auto mt-12 max-w-3xl">
              <h2 className="mb-4 text-2xl font-bold text-zinc-900">About This Product</h2>
              <div
                className="prose prose-zinc max-w-none prose-headings:text-zinc-900 prose-p:text-zinc-600 prose-a:text-red-600"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
